import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ImFinancialViz } from "../components/ImFinancialViz";
import { AuthImage, IM_SLIDES, imagesBySlide } from "../components/ImStrategyForm";
import { ProformaView } from "../components/Proforma";
import { ArrowLeft, Lock, ShieldCheck, MapPin, Eye } from "lucide-react";
import TeaserMarkdown from "../components/TeaserMarkdown";
import { ImPdfButton } from "../components/ImPdfButton";
import { t } from "../lib/i18n";

const IM_SECTIONS = IM_SLIDES;

export default function ImView() {
  const { companyId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const isPreview = searchParams.get("preview") === "1" && data?.is_owner;
  const slideImages = imagesBySlide(data?.images);

  useEffect(() => {
    api.get(`/companies/${companyId}/im`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const d = e.response?.data?.detail;
        setError(typeof d === "string" ? d : "No tienes acceso a este documento.");
      });
  }, [companyId]);

  const backPath =
    isPreview ? `/seller/company/${companyId}/im` :
    user?.role === "searcher" ? `/searcher/company/${companyId}` :
    user?.role === "investor" ? `/investor/opportunity/${companyId}` :
    `/seller/company/${companyId}`;

  if (error) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="im-access-denied">
          <div className="w-14 h-14 hairline mx-auto flex items-center justify-center bg-bone mb-6">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="font-serif-display text-3xl mb-3">{t("Acceso restringido")}</h1>
          <p className="text-muted-foreground mb-8">{error}</p>
          <Link to={backPath} className="font-mono-label text-gold inline-flex items-center gap-2" data-testid="im-denied-back">
            <ArrowLeft className="w-3 h-3" />{" "}{t("Volver")}
          </Link>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-16" data-testid="im-view-loading">
          <div className="h-4 w-24 bg-muted animate-pulse mb-8" />
          <div className="h-4 w-48 bg-muted animate-pulse mb-3" />
          <div className="h-12 w-2/3 bg-muted animate-pulse mb-2" />
          <div className="h-4 w-32 bg-muted animate-pulse mb-8" />
          <div className="hairline bg-card">
            <div className="h-12 bg-[#1E3A5F] animate-pulse" />
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="px-7 py-6 border-b border-border/60 space-y-3">
                <div className="h-6 w-56 bg-muted animate-pulse" />
                <div className="h-3 w-full bg-muted animate-pulse" />
                <div className="h-3 w-5/6 bg-muted animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {isPreview && (
        <div className="bg-[#E0712C] text-white" data-testid="im-preview-banner">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Eye className="w-4 h-4" />
              <span className="font-mono-label">{t("IM publicado · vista del inversor")}</span>
              <span className="text-sm text-white/90">
                {t("Esto es exactamente lo que ven searchers e inversores con NDA aprobado")}{data.im_published ? "." : "cuando lo publiques."}{" "}{t("Aquí no se puede editar.")}
              </span>
            </div>
            <Link to={backPath} className="font-mono-label underline hover:opacity-80" data-testid="im-preview-back-editor">
              {t("Volver al IM editable")}
            </Link>
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
        <Link
          to={backPath}
          className="font-mono-label text-muted-foreground inline-flex items-center gap-2 mb-8 hover:text-foreground"
          data-testid="im-view-back"
        >
          <ArrowLeft className="w-3 h-3" /> {isPreview ? t("Volver al IM editable") : t("Volver")}
        </Link>

        <div className="hairline bg-[#1E3A5F] text-bone p-8 md:p-10 mb-0" data-testid="im-hero">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-4 h-4 text-gold" />
              <span className="font-mono-label text-gold">{t("Information Memorandum · Confidencial bajo NDA")}</span>
            </div>
            <ImPdfButton companyId={data.company_id} companyName={data.name} variant="light" />
          </div>
          <h1 className="font-serif-display text-4xl md:text-6xl leading-tight text-bone" data-testid="im-company-name">{data.name}</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className="font-mono-label text-bone/70">{data.sector}</span>
            <span className="text-bone/40">·</span>
            <span className="font-mono-label text-bone/70 inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {data.country}
            </span>
          </div>
        </div>

          {(data.projection || data.kpis?.revenue) && (
          <ImFinancialViz kpis={data.kpis} projection={data.proforma?.years?.length ? null : data.projection} />
        )}

        {data.proforma?.years?.length > 0 && <ProformaView proforma={data.proforma} />}

        <div className="hairline hairline-t-0 bg-card">
          <Tabs defaultValue="executive_summary" className="w-full">
            <TabsList className="w-full justify-start rounded-none h-auto p-0 bg-bone hairline-b flex-wrap">
              {IM_SECTIONS.map((s) => (
                <TabsTrigger
                  key={s.key}
                  value={s.key}
                  className="rounded-none font-mono-label border-b-2 border-transparent data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border-[#E0712C] data-[state=active]:shadow-none px-5 py-3.5 text-muted-foreground"
                  data-testid={`im-view-trigger-${s.key}`}
                >
                  {s.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {IM_SECTIONS.map((s) => {
              const imgs = slideImages[s.key];
              return (
                <TabsContent key={s.key} value={s.key} className="px-7 py-8 mt-0">
                  <h2 className="font-serif-display text-3xl mb-5">{s.label}</h2>
                  <div className={imgs.length ? "grid lg:grid-cols-12 gap-8" : ""}>
                    <article className={`teaser-prose ${imgs.length ? "lg:col-span-8" : ""}`} data-testid={`im-view-content-${s.key}`}>
                      <TeaserMarkdown text={data.sections?.[s.key] || "—"} stripLeadingHeading />
                    </article>
                    {imgs.length > 0 && (
                      <aside className="lg:col-span-4 space-y-3" data-testid={`im-slide-images-${s.key}`}>
                        {imgs.map((img) => (
                          <AuthImage
                            key={img.image_id}
                            companyId={data.company_id}
                            imageId={img.image_id}
                            className="w-full h-44 hairline"
                          />
                        ))}
                      </aside>
                    )}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>

        <p className="font-mono-label text-muted-foreground mt-6" style={{ fontSize: "0.6rem" }}>
          {t("Las proyecciones son estimaciones basadas en hipótesis del vendedor y no constituyen garantía de resultados. Este documento está protegido por el NDA firmado en plataforma; su distribución fuera de Relevo constituye un incumplimiento del acuerdo de confidencialidad.")}
        </p>
      </div>
    </Layout>
  );
}
